import { useState } from "react";
import { Flex, Radio, Typography } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { translate, translateWithVars } from "shared/languages";
import { localDateOnly } from "shared/utils/dayjs";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

export type EffectiveMonth = "this_month" | "next_month";

// "Berlaku mulai" for a frequency change. Shown only when the server asks,
// which is whenever the studio has runs: the app cannot tell which month the
// owner meant. Once a run already touches this month the server closes that
// option (`thisMonthAllowed` false); it is still shown, greyed, with the
// reason on it, so the owner sees why the change can only start next month
// rather than the save silently landing a month later.
//
// Choice cards rather than the pill pair the Gaji Pokok and Item Berulang
// writes use: those switch an inline form between two periods, this one is a
// decision taken in a dialog and confirmed, and each option changes which
// month a studio's payroll is cut on, so each says what it will do.
const EffectiveMonthPicker = ({
  thisMonth,
  nextMonth,
  thisMonthAllowed = true,
  onChange,
}: {
  thisMonth: string; // "YYYY-MM-DD"
  nextMonth: string;
  thisMonthAllowed?: boolean;
  onChange: (value: EffectiveMonth) => void;
}) => {
  const [value, setValue] = useState<EffectiveMonth>("next_month");
  // Both dates arrive as a bare Jakarta calendar day, so they are formatted
  // with localDateOnly, never localTzDate: dayjs parses a date-only string in
  // the BROWSER's zone, which on a machine east of Jakarta (Bali/WITA +8,
  // Papua/WIT +9) lands a day early and would offer "30 Sep" for 1 Oct.
  const day = (date: string) => localDateOnly(date).format("D MMM YYYY");

  // A closed option keeps its place and carries the reason it is closed
  // (house rule: a disabled control says why), in place of its usual line.
  const option = (key: EffectiveMonth, date: string, disabled = false) => (
    <Radio value={key} disabled={disabled}>
      <Flex vertical gap={2}>
        <Typography.Text style={{ fontWeight: 500, color: disabled ? colors.ink3 : colors.ink }}>
          {translateWithVars(`payrollPage.settings.effective_${key}`, { date: day(date) })}
        </Typography.Text>
        <Typography.Text style={{ fontSize: textPx(13), color: disabled ? colors.ink3 : colors.ink2 }}>
          {disabled
            ? translate("payrollPage.settings.effective_this_month_closed_desc")
            : translateWithVars(`payrollPage.settings.effective_${key}_desc`, { date: day(date) })}
        </Typography.Text>
      </Flex>
    </Radio>
  );

  // The dialog's own title already asks when the change starts, so there is
  // no second label here; the explainer says why the question is being asked
  // at all. marginTop gives the title room to breathe (house rule: never the
  // cramped 8px default between a modal's title and its body).
  return (
    <Flex vertical gap={12} style={{ marginTop: 10 }}>
      {/* The explainer under the title answers the question being asked. It
          is only true while this month is open; when it is closed the reason
          sits on the greyed option itself and nothing prints here. */}
      {thisMonthAllowed && (
        <Typography.Text type="secondary" style={{ fontSize: textPx(13), lineHeight: 1.45, color: colors.ink2 }}>
          {translate("payrollPage.settings.effective_hint")}
        </Typography.Text>
      )}
      <Radio.Group
        className="choice-cards"
        style={{ width: "100%" }}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
      >
        <Flex vertical gap={8}>
          {option("this_month", thisMonth, !thisMonthAllowed)}
          {option("next_month", nextMonth)}
        </Flex>
      </Radio.Group>
      {/* The salary fact is separate from the question, so it sits under the
          options as the last line before confirming, never as the explainer
          (where it read as the reason the question was asked). Bold on
          purpose: it is the one reminder the owner must not skim past, and
          down here it competes with nothing (chris, 2026-10-13). */}
      <Flex align="center" gap={8}>
        {/* The same filled icon the payroll warning boxes lead with, so the
            line reads as one of those reminders even without a box. Amber,
            not navy: this asks the owner to act on the salaries. */}
        <ExclamationCircleFilled style={{ fontSize: textPx(14), color: colors.amberDark, flexShrink: 0 }} />
        <Typography.Text style={{ fontSize: textPx(13), fontWeight: 600, lineHeight: 1.45, color: colors.ink }}>
          {translate("payrollPage.settings.salary_unchanged")}
        </Typography.Text>
      </Flex>
    </Flex>
  );
};

export default EffectiveMonthPicker;
